import { cn } from 'utils';
import { Breakpoint } from 'types/utils';
import { Caption, Textarea } from 'fronton-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReviewBodyTextProps,
  ReviewConsTextProps,
  ReviewFormFieldError,
  ReviewProsTextProps,
} from '../types';
import { TextareaField } from './layout-blocks';
import { reviewTextArea, reviewTextAreaTabletMinus } from './review-text-part.styles';

export const ReviewTextFormPart: React.FC<
  ReviewBodyTextProps &
    ReviewProsTextProps &
    ReviewConsTextProps & {
      errors: ReviewFormFieldError[] | null;
    } & {
      breakpoint: Breakpoint;
    }
> = ({
  body,
  pros,
  cons,
  errors,
  breakpoint,
  setReviewProsText,
  setReviewBodyText,
  setReviewConsText,
  onBodyFieldFocus,
  onProsFieldFocus,
  onConsFieldFocus,
}) => {
  const { t } = useTranslation();
  const tInput1 = t('reviews.form.review.input1');
  const tInput2 = t('reviews.form.review.input2');
  const tInput3 = t('reviews.form.review.input3');
  const doSetReviewBoby = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => setReviewBodyText(event.target.value),
    [setReviewBodyText],
  );
  const doSetReviewPros = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => setReviewProsText(event.target.value),
    [setReviewProsText],
  );
  const doSetReviewCons = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => setReviewConsText(event.target.value),
    [setReviewConsText],
  );

  const bodyTextLabel = typeof body === 'string' && body.length > 0 ? tInput1 : undefined;
  const bodyTextLength = typeof body === 'string' ? body.length : 0;

  const prosTextLabel = typeof pros === 'string' && pros.length > 0 ? tInput2 : undefined;
  const prosTextLength = typeof pros === 'string' ? pros.length : 0;

  const consTextLabel = typeof cons === 'string' && cons.length > 0 ? tInput3 : undefined;
  const consTextLength = typeof cons === 'string' ? cons.length : 0;

  const bodyErrorCaption = errors?.find((r) => r.fieldKey === 'body')?.message;
  const prosErrorCaption = errors?.find((r) => r.fieldKey === 'pros')?.message;
  const consErrorCaption = errors?.find((r) => r.fieldKey === 'cons')?.message;

  const onBodyFieldFocusCb = React.useCallback(() => onBodyFieldFocus(), [onBodyFieldFocus]);
  const onProsFieldFocusCb = React.useCallback(() => onProsFieldFocus(), [onProsFieldFocus]);
  const onConsFieldFocusCb = React.useCallback(() => onConsFieldFocus(), [onConsFieldFocus]);

  const tabletMinusBreakpoints = ['mobile', 'tablet'].includes(breakpoint);

  return (
    <>
      <TextareaField
        textarea={
          <Textarea
            className={cn(reviewTextArea, {
              [reviewTextAreaTabletMinus]: tabletMinusBreakpoints,
            })}
            size="m"
            label={bodyTextLabel}
            placeholder={tInput1}
            name="body"
            value={body ?? undefined}
            onChange={doSetReviewBoby}
            onFocus={onBodyFieldFocusCb}
            isError={bodyErrorCaption !== undefined}
          />
        }
        errorCaption={bodyErrorCaption !== undefined ? <Caption error message={bodyErrorCaption} /> : null}
        textLength={bodyTextLength}
        dataQa="text-area-general-container"
      />

      <TextareaField
        textarea={
          <Textarea
            className={cn(reviewTextArea, {
              [reviewTextAreaTabletMinus]: tabletMinusBreakpoints,
            })}
            size="m"
            label={prosTextLabel}
            placeholder={tInput2}
            name="pros"
            value={pros ?? undefined}
            onChange={doSetReviewPros}
            onFocus={onProsFieldFocusCb}
            isError={prosErrorCaption !== undefined}
          />
        }
        errorCaption={prosErrorCaption !== undefined ? <Caption error message={prosErrorCaption} /> : null}
        textLength={prosTextLength}
        dataQa="text-area-advantages-container"
      />

      <TextareaField
        textarea={
          <Textarea
            className={cn(reviewTextArea, {
              [reviewTextAreaTabletMinus]: tabletMinusBreakpoints,
            })}
            size="m"
            label={consTextLabel}
            placeholder={tInput3}
            name="cons"
            value={cons ?? undefined}
            onChange={doSetReviewCons}
            onFocus={onConsFieldFocusCb}
            isError={consErrorCaption !== undefined}
          />
        }
        errorCaption={consErrorCaption !== undefined ? <Caption error message={consErrorCaption} /> : null}
        textLength={consTextLength}
        dataQa="text-area-flaws-container"
      />
    </>
  );
};
